import { ensureDir } from '@/utils/fs/fs-extra.utils';

export interface AppInfo {
  name?: string;
  isPackaged?: boolean;
  platform?: NodeJS.Platform;
  version: string;
  appPath: string;
  versions: {
    chrome: string;
    electron: string;
    node: string;
  };
}

/**
 * 定义和electron交互的方法
 */
export class ElectronApi {
  /**
   * 返回os
   */
  static get os() {
    return window.electron?.os;
  }

  /**
   * 返回fs
   */
  static get fs() {
    return window.electron?.fs;
  }

  /**
   * 返回shell
   */
  static get shell() {
    return window.electron?.shell;
  }

  /**
   * 返回path
   */
  static get path() {
    return window.electron?.path;
  }

  /**
   * 返回ipcRenderer
   */
  static get ipcRenderer() {
    return window.electron?.ipcRenderer;
  }

  static async getAppInfo(): Promise<AppInfo> {
    return (await ElectronApi.ipcRenderer?.invoke('get_app_info')) ?? {};
  }

  /**
   * 打开目录
   * @param dir
   * @returns
   */
  static async showItemInFolder(dir: string) {
    try {
      await ElectronApi.shell?.showItemInFolder(dir);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * 执行adb或aapt命令
   */
  static async execAdbFile(command: string[]): Promise<{ success: boolean }> {
    return await ElectronApi.ipcRenderer?.invoke('exec_adb_file', {
      command
    });
  }

  /**
   * 打开devtools
   */
  static async openDevtools() {
    await ElectronApi.ipcRenderer?.invoke('open_devtools');
  }

  /**
   * 获取临时目录
   */
  static async getPath(
    pathKey:
      | 'home'
      | 'appData'
      | 'userData'
      | 'sessionData'
      | 'temp'
      | 'exe'
      | 'module'
      | 'desktop'
      | 'documents'
      | 'downloads'
      | 'music'
      | 'pictures'
      | 'videos'
      | 'recent'
      | 'logs'
      | 'crashDumps'
  ) {
    const isTemp = pathKey == 'temp';
    pathKey = isTemp ? 'userData' : pathKey;
    let dir = await ElectronApi.ipcRenderer?.invoke('get_path', { pathKey });
    // 修改temp目录
    if (isTemp) {
      dir = ElectronApi.path?.join(dir, 'MyTemp2');
      await ensureDir(dir);
    }
    return dir as string;
  }

  /**
   * 打开浏览器
   */
  static async openExternalLink(link: string) {
    return await ElectronApi.shell?.openExternal(link);
  }

  /**
   * 返回默认的保存目录
   *
   * @static
   * @memberof ElectornApi
   */
  static async getWatchFaceDir() {
    const dir = ElectronApi.path?.join(
      await ElectronApi.getPath('desktop'),
      'WatchFace'
    );
    if (dir) await ensureDir(dir);
    return dir;
  }

  /**
   * close窗口
   */
  static async close() {
    await ElectronApi.ipcRenderer?.invoke('close');
  }
  /**
   * exit程序
   */
  static async exit() {
    await ElectronApi.ipcRenderer?.invoke('exit');
  }

  /**
   * quit
   */
  static async reload() {
    await ElectronApi.ipcRenderer?.invoke('reload');
  }

  /**
   * minimize
   */
  static async minimize() {
    await ElectronApi.ipcRenderer?.invoke('minimize');
  }

  /**
   * 执行本地程序
   */
  static async execFile(execFilename: string, commands: string[]) {
    try {
      return await ElectronApi.ipcRenderer?.invoke('exec-file', {
        execFilename,
        commands
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * 返回版本类型： 专业版、测试版、普通版
   */
  static versionType() {
    return window.electron?.versionType ?? 'normal';
  }

  /**
   * 返回是否专业版（内部版本）
   */
  static isProfessional() {
    return window.electron?.versionType == 'professional';
  }
  /**
   * 返回是否测试版
   */
  static isStaging() {
    return window.electron?.versionType == 'staging';
  }

  /**
   * 返回是否debug版本
   */
  static isDebug() {
    return (
      !ElectronApi.isNormal() &&
      window.location.href.toLowerCase().includes('debug')
    );
  }
  /**
   * 返回是否普通版本（对外版本）
   */
  static isNormal() {
    return !ElectronApi.isProfessional() && !ElectronApi.isStaging();
  }
}
